import type { RootState } from './interfaces/RootState';
import type { SearchPage, SearchStoreState } from './interfaces/SearchStoreState';
import type { SearchResponse } from '@/interfaces/Search';
import RequestService from '@/common/requestService';
import Utility from '@/common/UtilityFunctions.js';
import _ from 'lodash';
import type { ActionTree, Commit, MutationTree } from 'vuex';

const initialState: SearchStoreState = {
  isSearching: false,
  isSearchingForAutosuggestions: false,
  barShown: false,
  searchTerm: '',
  totalResults: 0,
  totalPages: 0,
  currentOffset: 0,
  pages: {},
  resultsPerPage: 10,
  currentPageNumber: 1,
  limit: 50,
  userUuid: ''
  // autosuggestions: []
};

const getSearchEndpoint = (searchTerm: string, limit: number, offset: number, uuid: string) => {
  const queryParameters = {
    searchTerm,
    limit: limit.toString(),
    offset: offset.toString(),
    uuid
  };

  const querystring = new URLSearchParams(queryParameters).toString();
  return `${Utility.constants().API.sitecore_search}?${querystring}`;
};

const getPages = (
  response: SearchResponse,
  offset: number,
  resultsPerPage: number,
  existingPages?: { [pageNumber: number]: SearchPage }
) => {
  const { results } = response;

  const pages: { [pageNumber: string]: SearchPage } = existingPages ?? {};

  let resultOffset = offset;

  results.forEach((result) => {
    const pageNumber = Math.floor(resultOffset / resultsPerPage) + 1;
    const pageExists = pages[pageNumber] !== undefined;

    if (pageExists) pages[pageNumber].results.push(result);
    else
      pages[pageNumber] = {
        isFetching: false,
        results: [result]
      };

    resultOffset++;
  });

  return pages;
};

const dispatchViewEvent = (searchTerm: string, entityIds: string[], uuid: string) => {
  if (!window.lbConsentHandeling.hasAllConsent()) return;

  const queryParameters = {
    keyword: searchTerm,
    searchResultPageUri: window.location.href,
    searchResultPageTitle: `S�geresultater ${searchTerm}`,
    uuid
  };

  const queryString = new URLSearchParams(queryParameters).toString();
  const requestUri = `${Utility.constants().API.sitecore_search_event_view}?${queryString}`;
  RequestService.post(requestUri, entityIds);
};

// See: https://doc.sitecore.com/search/en/developers/search-developer-guide/using-a-uuid-to-track-site-visitors.html
const generateSearchUserUuid = () => {
  const digit2string = (i: number): string =>
    String.fromCharCode(i < 10 ? i + 48 : i < 36 ? i + 87 : i + 29);

  const uuidx = 'xx-xx-4x-1p-'; // pattern of prefix to user id, where r is a replaceable random token
  let uuid: string = uuidx.replace(/[x]/g, () => digit2string(Math.floor(Math.random() * 36)));
  for (let i = 0; i < 5; i++) {
    uuid += ('0000' + Math.floor(Math.random() * 1679615).toString(36)).slice(-4);
  }
  const domainId = '176607864';
  const value = domainId + '-' + uuid + '-' + Date.now();

  return value;
};

const getOrSetUserUuid = (state: SearchStoreState, commit: Commit) => {
  let userUuid: string;
  if (state.userUuid) userUuid = state.userUuid;
  else {
    userUuid = generateSearchUserUuid();
    commit('setUserUuid', userUuid);
  }

  return userUuid;
};

const actions: ActionTree<SearchStoreState, RootState> = {
  toggleBar({ commit }) {
    commit('toggleBar');
  },
  changePage({ state, commit }, pageNumber: number) {
    const page = state.pages[pageNumber];
    const entityIds = page.results.map((r) => r.id);
    dispatchViewEvent(state.searchTerm, entityIds, state.userUuid);
    commit('setPageNumber', pageNumber);
  },
  // resetAutosuggestions({ commit }) {
  //     commit('setAutosuggestions', []);
  // },
  async performSearch({ state, commit }, searchTerm: string) {
    commit('setSearchTerm', searchTerm);
    commit('setIsSearching', true);
    const offset = 0;
    const uuid = getOrSetUserUuid(state, commit);
    const searchEndpoint = getSearchEndpoint(searchTerm, state.limit, offset, uuid);

    if (searchTerm)
      RequestService.get<SearchResponse>(searchEndpoint)
        .then((response) => {
          const searchResponse = response.data;
          const pages = getPages(searchResponse, offset, state.resultsPerPage);
          const entityIds = pages[1] ? pages[1].results.map((r) => r.id) : [];
          dispatchViewEvent(searchTerm, entityIds, uuid);
          const totalPages = Math.floor(searchResponse.totalResults / state.resultsPerPage) + 1;

          commit('setSearchPages', { pages, offset: state.limit });
          commit('setTotalPages', totalPages);
        })
        .catch((e) => {
          Utility.logError(e, this);
          commit('setSearchPages', { pages: {}, offset: 0 });
          commit('setTotalPages', 0);
          commit('setIsSearching', false);
        });
    else commit('resetResults');
    commit('setPageNumber', 1);
  },
  async fetchPages({ state, commit }, pageNumber: number) {
    const uuid = getOrSetUserUuid(state, commit);
    const searchEndpoint = getSearchEndpoint(
      state.searchTerm,
      state.limit,
      state.currentOffset,
      uuid
    );

    await RequestService.get<SearchResponse>(searchEndpoint)
      .then((response) => {
        const searchResponse = response.data;
        const currentPages = _.cloneDeep(state.pages);
        const pages = getPages(
          searchResponse,
          state.currentOffset,
          state.resultsPerPage,
          currentPages
        );
        const newOffset = state.currentOffset + state.limit;

        commit('setPageNumber', pageNumber);
        commit('setSearchPages', { pages, offset: newOffset });
      })
      .catch((e) => {
        Utility.logError(e, this);
        commit('setIsSearching', false);
      });
  }
  // async searchAutosuggestions({ commit }, searchTerm: string) {
  //     commit('setIsSearchingForAutosuggestions');
  //     const maxSuggestions = 4;
  //     const autosuggestions = await SearchService.searchAutosuggestions(searchTerm, maxSuggestions);

  //     commit('setAutosuggestions', autosuggestions);
  // },
};

const mutations: MutationTree<SearchStoreState> = {
  toggleBar(state: SearchStoreState) {
    state.barShown = !state.barShown;
  },
  setPageNumber(state: SearchStoreState, pageNumber: number) {
    state.currentPageNumber = pageNumber;
  },
  setUserUuid(state: SearchStoreState, userUuid: string) {
    state.userUuid = userUuid;
  },
  setSearchTerm(state: SearchStoreState, searchTerm: string) {
    state.searchTerm = searchTerm;
  },
  setSelected(state: SearchStoreState, object) {
    state.searchTerm = object.selected.Title;
    const redirectLink = `${object.url}?q=${state.searchTerm}`;
    window.location.assign(redirectLink);
  },
  resetSearchTerm(state: SearchStoreState) {
    state.searchTerm = '';
  },
  setSearchPages(
    state: SearchStoreState,
    { pages, offset }: { pages: { [pageNumber: number]: SearchPage }; offset: number }
  ) {
    state.pages = pages;
    state.currentOffset = offset;
    state.isSearching = false;
  },
  setTotalPages(state: SearchStoreState, totalPages: number) {
    state.totalPages = totalPages;
  },
  setIsSearching(state: SearchStoreState, isSearching: boolean) {
    state.isSearching = isSearching;
  },
  resetResults(state: SearchStoreState) {
    state.pages = [];
    state.currentOffset = 0;
    state.isSearching = false;
    state.totalPages = 0;
  }
  // setAutosuggestions(state: SearchStoreState, autosuggestions: string[]) {
  //     state.autosuggestions = autosuggestions;
  //     state.isSearchingForAutosuggestions = false;
  // },
  // setIsSearchingForAutosuggestions(state: SearchStoreState) {
  //     state.autosuggestions = [];
  //     state.isSearchingForAutosuggestions = true;
  // }
};

export default {
  namespaced: true,
  state: initialState,
  actions,
  mutations
};
